// frontend/src/components/playground/PlaygroundGuide.jsx
/**
 * Playground Guide - Character that explains the game and provides advice
 * A friendly AI assistant that helps users learn and achieve their goals
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SparklesIcon,
  XMarkIcon,
  LightBulbIcon,
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  CheckCircleIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import PlaygroundCharacter from './PlaygroundCharacter';
import CharacterPreferenceModal from './CharacterPreferenceModal';

const PlaygroundGuide = ({ 
  user, 
  gameState, 
  onAction, 
  currentStep = null 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [forceVisible, setForceVisible] = useState(false); // Force visible after simulation
  const [messages, setMessages] = useState([]);
  const [currentAdvice, setCurrentAdvice] = useState(null);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(false);
  const [characterGender, setCharacterGender] = useState('neutral');
  const [characterName, setCharacterName] = useState('Aura');
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [characterExpression, setCharacterExpression] = useState('happy');
  const messagesEndRef = useRef(null);

  // Load character preference
  useEffect(() => {
    const savedPreference = localStorage.getItem('playground_character_preference');
    
    if (!savedPreference) {
      // First time - show character selection modal
      setTimeout(() => {
        setShowCharacterModal(true);
      }, 1000);
      setCharacterGender('neutral');
      setCharacterName('Aura');
    } else {
      setCharacterGender(savedPreference);
      
      // Set character name based on gender
      if (savedPreference === 'male') {
        setCharacterName('Alex');
      } else if (savedPreference === 'female') {
        setCharacterName('Sam');
      } else {
        setCharacterName('Aura');
      }
    }
  }, []);

  useEffect(() => {
    // Check if user has seen tutorial
    const tutorialSeen = localStorage.getItem('playground_tutorial_seen');
    setHasSeenTutorial(!!tutorialSeen);
    
    if (!tutorialSeen) {
      // Show welcome message after character is loaded
      const timer = setTimeout(() => {
        showWelcomeMessage();
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // Show contextual help based on game state
      showContextualHelp();
    }
  }, [characterName]);

  useEffect(() => {
    // Update messages when game state changes
    if (hasSeenTutorial && gameState) {
      if (gameState.simulationResult) {
        // Force guide to be visible when simulation completes
        setIsMinimized(false);
        setForceVisible(true);
        explainSimulationResult(gameState.simulationResult);
        // Auto-hide force visible after 5 seconds
        setTimeout(() => setForceVisible(false), 5000);
      } else {
        showContextualHelp();
      }
    }
  }, [gameState?.simulationResult, gameState?.cards?.length]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleCharacterSelect = (gender) => {
    setCharacterGender(gender);
    if (gender === 'male') {
      setCharacterName('Alex');
    } else if (gender === 'female') {
      setCharacterName('Sam');
    } else {
      setCharacterName('Aura');
    }
  };

  const showWelcomeMessage = () => {
    const welcomeMessages = [
      {
        id: 1,
        type: 'guide',
        content: `Hi ${user?.full_name || 'there'}! 👋 I'm ${characterName}, your financial mentor. Welcome to the Playground!`,
        timestamp: new Date()
      },
      {
        id: 2,
        type: 'guide',
        content: `This is where you can safely experiment with investment strategies. Think of it like a financial simulator - you can try different moves without risking real money!`,
        timestamp: new Date()
      },
      {
        id: 3,
        type: 'guide',
        content: `Here's how the Playground works:\n\n🎴 **Cards**: You get 3 cards each day based on current market news. Each card represents a real investment strategy.\n\n📊 **Play Cards**: Click the "Play Card" button or drag a card onto the Portfolio area to simulate what would happen.\n\n📈 **See Results**: After playing a card, you'll see:\n   • How your portfolio value changes\n   • Impact on your monthly income\n   • How it affects your Vision goals\n   • Risk level changes\n\n✅ **Make it Real**: If you like the simulation result, click "Make it Real" to execute the strategy in your actual portfolio!`,
        timestamp: new Date()
      },
      {
        id: 4,
        type: 'guide',
        content: `Ready to start? Try playing one of your cards! I'll explain everything that happens step by step. 💫\n\nI'm always here to help - click "Get Advice" anytime or ask me questions about the game!`,
        timestamp: new Date()
      }
    ];
    
    setMessages(welcomeMessages);
  };

  const showContextualHelp = () => {
    if (!gameState) return;
    
    // Show help based on current state
    if (gameState.cards && gameState.cards.length > 0 && !gameState.simulationResult) {
      const unplayedCards = gameState.cards.filter(c => !c.played).length;
      if (unplayedCards > 0) {
        setMessages(prev => {
          // Don't add duplicate messages
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && (lastMessage.content.includes('cards ready to play') || lastMessage.content.includes('move'))) {
            return prev;
          }
          
          let message = `You have ${unplayedCards} card${unplayedCards > 1 ? 's' : ''} ready to play! `;
          
          if (gameState.movesRemaining > 0) {
            message += `You have ${gameState.movesRemaining} move${gameState.movesRemaining > 1 ? 's' : ''} left today. `;
            message += `\n\n💡 **How to play**:\n`;
            message += `1. Click the "Play Card" button on any card\n`;
            message += `2. Or drag a card onto the Portfolio area\n`;
            message += `3. I'll explain what happens to your portfolio! 📊`;
          } else {
            message += `No moves left - come back tomorrow for new cards! 🔄`;
          }
          
          return [...prev, {
            id: Date.now(),
            type: 'guide',
            content: message,
            timestamp: new Date()
          }];
        });
      }
    }
  };

  const explainSimulationResult = (result) => {
    const portfolioDelta = result.portfolio_delta || {};
    const visionImpacts = result.vision_impacts || [];
    
    // Change expression based on result
    if ((portfolioDelta.value_change || 0) > 0) {
      setCharacterExpression('excited');
    } else if ((portfolioDelta.value_change || 0) < 0) {
      setCharacterExpression('thinking');
    } else {
      setCharacterExpression('calm');
    }
    
    let explanation = `🎉 Great move! Here's what happened with your strategy:\n\n`;
    
    // Portfolio value change
    if ((portfolioDelta.value_change || 0) > 0) {
      explanation += `✅ **Portfolio Value**: Increased by $${Math.abs(portfolioDelta.value_change || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
      if (portfolioDelta.value_change_pct) {
        explanation += `   That's a ${portfolioDelta.value_change_pct.toFixed(2)}% increase! 🚀\n`;
      }
    } else if ((portfolioDelta.value_change || 0) < 0) {
      explanation += `⚠️ **Portfolio Value**: Decreased by $${Math.abs(portfolioDelta.value_change || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
      if (portfolioDelta.value_change_pct) {
        explanation += `   That's a ${Math.abs(portfolioDelta.value_change_pct).toFixed(2)}% decrease. This could be due to market volatility.\n`;
      }
    } else {
      explanation += `➡️ **Portfolio Value**: Stayed the same\n`;
    }
    
    explanation += `\n`;
    
    // Income change
    if ((portfolioDelta.income_change || 0) > 0) {
      explanation += `💰 **Monthly Income**: Increased by $${(portfolioDelta.income_change || 0).toFixed(2)}/month\n`;
      explanation += `   That's $${((portfolioDelta.income_change || 0) * 12).toFixed(2)} extra per year! 💵\n`;
    } else if ((portfolioDelta.income_change || 0) < 0) {
      explanation += `📉 **Monthly Income**: Decreased by $${Math.abs(portfolioDelta.income_change || 0).toFixed(2)}/month\n`;
    } else {
      explanation += `➡️ **Monthly Income**: No change\n`;
    }
    
    // Vision impacts
    if (visionImpacts && visionImpacts.length > 0) {
      explanation += `\n🎯 **Vision Goals Impact**:\n`;
      visionImpacts.forEach((impact, idx) => {
        const title = impact.title || impact.vision_id || `Goal ${idx + 1}`;
        const deltaYears = impact.delta_years || 0;
        
        if (deltaYears < 0) {
          explanation += `   ✅ **${title}**: ${Math.abs(deltaYears).toFixed(1)} years CLOSER to achieving! 🎯\n`;
        } else if (deltaYears > 0) {
          explanation += `   ⏳ **${title}**: ${deltaYears.toFixed(1)} years further away\n`;
        } else {
          explanation += `   ➡️ **${title}**: No change\n`;
        }
      });
    }
    
    // Risk score
    if (portfolioDelta.risk_change !== undefined) {
      explanation += `\n📊 **Risk Level**: `;
      if (portfolioDelta.risk_change > 0) {
        explanation += `Increased by ${portfolioDelta.risk_change.toFixed(1)} (higher risk, higher potential returns)\n`;
      } else if (portfolioDelta.risk_change < 0) {
        explanation += `Decreased by ${Math.abs(portfolioDelta.risk_change).toFixed(1)} (more conservative approach)\n`;
      } else {
        explanation += `No change\n`;
      }
    }
    
    explanation += `\n💡 **What this means**: This is a simulation showing what could happen if you applied this strategy. You can try different cards to see various outcomes before making real changes!`;
    
    setMessages(prev => {
      // Clear previous simulation explanation if exists, add new one
      const filtered = prev.filter(msg => !msg.content.includes('Great move!') && !msg.content.includes('what happened'));
      return [...filtered, {
        id: Date.now(),
        type: 'guide',
        content: explanation,
        timestamp: new Date()
      }];
    });
  };

  const getAdvice = async (question) => {
    try {
      // Use the enhanced vision AI agent for personalized advice
      const response = await axios.post('/api/vision-integration/ai-assistant/chat', {
        message: question || "What should I do in the Playground?",
        context: {
          type: 'playground',
          game_state: gameState
        }
      });
      
      const advice = response.data?.response || response.data?.message || 
        "I'm here to help! Try playing a card to see how it affects your financial visions. Each card represents a real investment strategy you can explore safely.";
      
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'guide',
        content: advice,
        timestamp: new Date()
      }]);
    } catch (err) {
      console.error('Error getting advice:', err);
      // Fallback advice
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'guide',
        content: "Remember: The Playground is your safe space to experiment. Try different cards to see what works best for your goals! 💫",
        timestamp: new Date()
      }]);
    }
  };

  const handleTutorialComplete = () => {
    localStorage.setItem('playground_tutorial_seen', 'true');
    setHasSeenTutorial(true);
    setIsMinimized(true);
  };

  const quickTips = [
    "💡 Tip: Dividend cards increase monthly income but may slow growth",
    "🚀 Growth cards can accelerate long-term goals but come with higher risk",
    "🛡️ Balance is key - don't put all your eggs in one basket",
    "📊 Watch your Vision progress bars to see how moves affect your goals",
    "🎯 The goal is to 'Secure' 3 visions - complete 3 financial goals!"
  ];

  if (!isVisible) return null;

  // Don't allow minimizing if force visible (after simulation)
  const shouldMinimize = isMinimized && !forceVisible;

  return (
    <motion.div
      initial={{ x: 100, opacity: 0 }}
      animate={{ x: shouldMinimize ? 300 : 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300 }}
      className="fixed bottom-6 right-6 z-50"
    >
      {shouldMinimize ? (
        <motion.button
          onClick={() => setIsMinimized(false)}
          className="w-16 h-16 rounded-full bg-gradient-to-br from-aurora-purple to-aurora-blue shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-transform overflow-hidden border-2 border-aurora-purple/50"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <PlaygroundCharacter 
            gender={characterGender} 
            size="sm"
            expression="happy"
            animated={false}
          />
        </motion.button>
      ) : (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-glass-card backdrop-blur-xl rounded-2xl border border-aurora-purple/30 shadow-2xl w-96 max-h-[600px] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-aurora-purple/20">
            <div className="flex items-center gap-3">
              <div className="relative">
                <PlaygroundCharacter 
                  gender={characterGender} 
                  size="md"
                  expression={characterExpression}
                  animated={false}
                />
                <button
                  onClick={() => setShowCharacterModal(true)}
                  className="absolute -bottom-1 -right-1 w-5 h-5 bg-aurora-purple rounded-full flex items-center justify-center border-2 border-cosmic-dark hover:scale-110 transition-transform"
                  title="Change character"
                >
                  <UserCircleIcon className="w-3 h-3 text-white" />
                </button>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">{characterName} Guide</h3>
                <p className="text-xs text-gray-400">Your financial mentor</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!forceVisible && (
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition"
                  title="Minimize guide"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
              {forceVisible && (
                <div className="px-2 py-1 text-xs text-aurora-purple bg-aurora-purple/20 rounded">
                  New Result!
                </div>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]">
            <AnimatePresence>
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-aurora-purple/10 rounded-lg p-3 border border-aurora-purple/20"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <PlaygroundCharacter 
                        gender={characterGender} 
                        size="sm"
                        expression={characterExpression}
                        animated={false}
                      />
                    </div>
                    <p className="text-sm text-gray-200 whitespace-pre-line flex-1">
                      {message.content}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Tips */}
          {!hasSeenTutorial && (
            <div className="p-4 border-t border-aurora-purple/20">
              <div className="flex items-center gap-2 mb-2">
                <LightBulbIcon className="w-4 h-4 text-aurora-gold" />
                <span className="text-xs font-semibold text-white">Quick Tips</span>
              </div>
              <div className="space-y-1">
                {quickTips.slice(0, 2).map((tip, idx) => (
                  <p key={idx} className="text-xs text-gray-300">{tip}</p>
                ))}
              </div>
              <button
                onClick={handleTutorialComplete}
                className="mt-3 w-full py-2 px-4 bg-gradient-to-r from-aurora-purple to-aurora-blue rounded-lg text-white text-sm font-semibold hover:opacity-90 transition"
              >
                Got it! Let's play
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="p-4 border-t border-aurora-purple/20 flex gap-2">
            <button
              onClick={() => getAdvice("What should I do next?")}
              className="flex-1 py-2 px-3 bg-aurora-blue/20 hover:bg-aurora-blue/30 text-aurora-blue rounded-lg text-sm font-medium transition"
            >
              Get Advice
            </button>
            <button
              onClick={() => {
                const randomTip = quickTips[Math.floor(Math.random() * quickTips.length)];
                setMessages(prev => [...prev, {
                  id: Date.now(),
                  type: 'guide',
                  content: randomTip,
                  timestamp: new Date()
                }]);
              }}
              className="flex-1 py-2 px-3 bg-aurora-purple/20 hover:bg-aurora-purple/30 text-aurora-purple rounded-lg text-sm font-medium transition"
            >
              Random Tip
            </button>
          </div>
        </motion.div>
      )}

      {/* Character Preference Modal */}
      <CharacterPreferenceModal
        isOpen={showCharacterModal}
        onClose={() => setShowCharacterModal(false)}
        onSelect={handleCharacterSelect}
        currentPreference={characterGender}
      />
    </motion.div>
  );
};

export default PlaygroundGuide;
